// WP-14 — ONE source of truth for marketing-site navigation.
// Every header (the sub-page MarketingTopBar, the Landing hero navbar's Explore menu, and
// every mobile panel) renders from these arrays. Never hand-maintain a second list — the
// desktop/mobile fork this replaces had already silently dropped Pricing and Switch from
// the mobile Explore menu.
import {
  ArrowLeftRight,
  Baby,
  BookOpen,
  Building2,
  CalendarCheck,
  Cable,
  Headset,
  LifeBuoy,
  MessageCircle,
  Network,
  Newspaper,
  Rocket,
  School,
  ScrollText,
  ShieldCheck,
  Smartphone,
  Sparkles,
} from "lucide-react";

// Top-level items for the shared marketing sub-page header. The desktop row (links + the
// Solutions/Resources dropdowns + Sign in/theme/CTAs) needs the xl/1280px breakpoint — at
// lg/1024px it no longer fits beside the wordmark, so 1024–1279px keeps the hamburger sheet
// (UX-10: this comment previously claimed lg/1024 while the implementation was already xl).
export const MARKETING_NAV = Object.freeze([
  { label: "Modules", to: "/modules" },
  { label: "Pricing", to: "/pricing" },
  { label: "Mobile app", to: "/mobile-app" },
  { label: "Switching", to: "/switch" },
]);

// Secondary destinations — rendered in the mobile panel and anywhere a fuller list is wanted.
// "Request a product tour" (the demo-intent contact mode) is deliberately labelled apart from
// the plain "Contact" entry — the two used to sit adjacent as "Product tour" / "Contact",
// two doors into one page with no hint of the difference (UX-11).
export const MARKETING_NAV_SECONDARY = Object.freeze([
  { label: "Aira AI", to: "/aira" },
  { label: "Request a product tour", to: "/contact?intent=product-tour" },
  { label: "Integrations", to: "/integrations" },
  { label: "Implementation — your first 90 days", to: "/implementation" },
  { label: "Guides & resources", to: "/resources" },
  { label: "Help Center", to: "/help" },
  { label: "Support & onboarding", to: "/support" },
  { label: "Contact", to: "/contact" },
  { label: "Security overview", to: "/security" },
]);

// UX-01/PGS-2 — the four audience segment pages. Rendered by the sub-page header's
// "Solutions" dropdown, the mobile panel's "Who it's for" group, and the footer Solutions
// column, so /solutions/* is never an orphan family again. App.js also treats this list as
// the set of LIVE segments (an unknown /solutions/:segment renders the marketing 404) —
// adding a segment page means adding its entry here.
export const SOLUTIONS_ITEMS = Object.freeze([
  { to: "/solutions/school", label: "K–12 schools", icon: School, desc: "The full connected school ERP", accent: "blue" },
  { to: "/solutions/preschool", label: "Preschools", icon: Baby, desc: "Parent-first early-years operations" },
  { to: "/solutions/college", label: "Colleges & universities", icon: Building2, desc: "Where Vidyapeeth360 fits for programmes and cohorts" },
  { to: "/solutions/coaching", label: "Coaching institutes", icon: BookOpen, desc: "Admissions, fees and attendance fit" },
  // PGS-15 — not a /solutions/:segment page, so App.js's live-segment gate (which matches only
  // `/solutions/<segment>` paths) never treats it as one; it simply rides the Solutions menus.
  { to: "/school-chains", label: "School groups & chains", icon: Network, desc: "One governed console across a trust's schools" },
]);

// UX-02/UX-12 — the sub-page header's "Resources" dropdown: the AI story, the demo-request
// path, support, and the trust pages an IT/committee reviewer needs — all one click from any
// marketing page instead of footer-only.
export const RESOURCE_ITEMS = Object.freeze([
  // SEO-12 — the /resources library. It leads the menu because it is the only entry here that is
  // actually reading material; the rest are product, trust and contact destinations.
  { to: "/resources", label: "Guides & resources", icon: Newspaper, desc: "Fees, DPDP, migration & buying guides", accent: "blue" },
  { to: "/aira", label: "Aira AI — guided demo", icon: Sparkles, desc: "Governed chat, voice & automation", accent: "blue" },
  { to: "/contact?intent=product-tour", label: "Request a product tour", icon: CalendarCheck, desc: "A guided walkthrough for your school" },
  { to: "/integrations", label: "Integrations", icon: Cable, desc: "Gateways, messaging channels & exports" },
  { to: "/implementation", label: "Implementation — your first 90 days", icon: Rocket, desc: "Register → import → verify → go live" },
  { to: "/help", label: "Help Center", icon: LifeBuoy, desc: "Guides & answers" },
  { to: "/support", label: "Support & onboarding", icon: Headset, desc: "Channels, escalation & what onboarding covers" },
  { to: "/contact", label: "Contact", icon: MessageCircle, desc: "Sales, onboarding & support channels" },
  { to: "/security", label: "Security overview", icon: ShieldCheck, desc: "Access, controls & audit evidence", accent: "blue" },
  { to: "/compliance/dpdp", label: "DPDP guide", icon: ScrollText, desc: "India's data-protection law for schools" },
]);

// The homepage "Explore" menu (desktop dropdown AND the mobile drawer render this same list).
// `to` = route Link; `scrollTo` = in-page section id on "/" (labelled as an on-page demo).
// Pricing is NOT listed here — it is already a top-level homepage nav link, and the duplicate
// entry read as a different destination (UX-11).
export const EXPLORE_ITEMS = Object.freeze([
  { to: "/aira", label: "Aira AI — guided demo", icon: Sparkles, desc: "Governed chat, voice & automation", accent: "blue" },
  { to: "/switch", label: "Switching & migration", icon: ArrowLeftRight, desc: "Move your data with a reviewed plan" },
  { to: "/mobile-app", label: "Mobile app", icon: Smartphone, desc: "The parent, student & staff app" },
  { to: "/help", label: "Help Center", icon: LifeBuoy, desc: "Guides & answers" },
  { to: "/security", label: "Security overview", icon: ShieldCheck, desc: "Trust pages for your IT reviewer", accent: "blue" },
]);

// WP-07 — the sitewide CTA vocabulary. Use these constants, never a hand-typed variant
// ("Start Free Trial" / "Start free" / "Start trial" / "Start your 90-day free trial" all
// coexisted before this).
export const CTA_PRIMARY_LABEL = "Start free trial";
// CRO-9: the sanctioned short form of CTA_PRIMARY_LABEL for width-constrained phone chrome —
// never invent another ("Start" alone told the visitor nothing).
export const CTA_PRIMARY_LABEL_SHORT = "Try free";
export const CTA_SECONDARY_LABEL = "Book a demo";
// POS-6 (2026-08-18): the ONE governed demo-CTA vocabulary. A surface may add a single
// approved context adjective through this helper — never a hand-typed variant ("Book Demo",
// "Book a Live Demo", "Book Free Demo" and friends all coexisted before this). The
// demoCtaVocabulary.contract.test.js jest contract fails on any new hand-typed variant.
// ("Book a migration walkthrough" on /switch is a distinct non-"demo" action label and is
// deliberately outside this vocabulary.)
export const DEMO_CTA_CONTEXTS = Object.freeze(["live", "free", "guided", "workflow"]);
export const demoCtaLabel = (context) =>
  (context && DEMO_CTA_CONTEXTS.includes(context) ? `Book a ${context} demo` : CTA_SECONDARY_LABEL);
export const DEMO_BOOKING_ROUTE = "/?intent=book-demo#see-vidyapeeth360-in-action";
